import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  combinedSearchThunk,
  loadingCombinedSearch,
  selectDrugResults,
  selectTopicQuery,
  selectTopicResults,
} from "../../../features/enhancedSearch";
import style from "./EnhancedSearchResults.module.scss";
import classNames from "classnames";
import PropTypes from "prop-types";
import { mapDrugs, summarizeDrugs } from "./mapper/mapDrugs";
import Loader from "../../Loader/Loader";
import AppSearchFilters from "../AppSearchFilters/AppSearchFilters";
import AnimatedPopup from "../../AnimatedComponents/AnimatedPopup";
import SearchResultsList from "./SearchResultList";
import useIsWebScreen from "../../../hooks/useScreenSize";
import { extractAllBoldText } from "../../../utils/extractAllBoldText";
import AdPhaFrameTester from "../AppDrugPage/AdPhaFrameTester";

const EnhancedSearchResults = ({
  search,
  selectedTab,
  setSelectedTab,
  isAppMode,
  isWebModernisationEnabled,
  navigate,
}) => {
  const dispatch = useDispatch();
  const previousSearchQuery = useSelector(selectTopicQuery);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [skipLoading, setSkipLoading] = useState(false);
  const [filterNumber, setFilterNumber] = useState(0);
  const [splitPaneEnabled, setSplitPaneEnabled] = useState(false);
  const [activeLink, setActiveLink] = useState(null);
  const { isDesktop } = useIsWebScreen();

  useEffect(() => {
    const searchParams = new URLSearchParams(search);
    if (searchParams.has("appendedinputbuttons")) {
      setSkipLoading(false);
      const urlSearch = searchParams.get("appendedinputbuttons");
      const guidelineFilter = searchParams.getAll("guidelinefilter");
      const searchWithin = searchParams.get("searchWithin");
      const filters = searchParams.getAll("guidelinefilter");
      if (filters.length) {
        setFilterNumber(filters.length);
      }
      setSearchTerm(urlSearch);
      dispatch(
        combinedSearchThunk({
          searchText: urlSearch,
          guidelineFacet: guidelineFilter?.length ? guidelineFilter : undefined,
          top: previousSearchQuery?.top,
          searchWithin: searchWithin || "",
        }),
      );
    }
  }, [search]);

  const drugResults = useSelector(selectDrugResults);
  const searchParams = new URLSearchParams(search);

  const isLoading = useSelector(loadingCombinedSearch);
  const remappedDrugs = mapDrugs(drugResults);
  const drugSummary = summarizeDrugs(drugResults);

  const remappedDrugResults = { results: remappedDrugs };
  const searchResults = [useSelector(selectTopicResults), remappedDrugResults];

  const noResultsMessage = selectedTab === 0 ? "topics" : "drug indications";
  const resultCount =
    selectedTab === 0
      ? searchResults[selectedTab]?.totalCount
      : searchResults[selectedTab]?.results?.length;
  let correctedTerm =
    searchParams.get("correction") ||
    extractAllBoldText(searchResults[0]?.results?.[0]?.title) ||
    extractAllBoldText(searchResults[0]?.results?.[0]?.summary) ||
    "";
  correctedTerm =
    correctedTerm.toLocaleLowerCase() === searchTerm.toLocaleLowerCase() ? "" : correctedTerm;
  const envName = (import.meta.env.VITE_ENV_NAME || import.meta.env.MODE || "").toLowerCase();
  const hostname = typeof window !== "undefined" ? window.location.hostname.toLowerCase() : "";
  const isStagingHost = hostname.includes("stage.") || hostname.includes("staging.");
  const isAdPhaTesterAllowedEnvironment =
    import.meta.env.DEV ||
    import.meta.env.MODE === "test" ||
    envName === "stage" ||
    envName === "staging" ||
    isStagingHost;
  const showAdPhaFrameTester =
    import.meta.env.VITE_ADPHA_FRAME_TESTER_ENABLED === "true" && isAdPhaTesterAllowedEnvironment;
  const isSplitPaneVisible =
    selectedTab === 1 &&
    showAdPhaFrameTester &&
    splitPaneEnabled &&
    isDesktop &&
    Boolean(activeLink);

  const openActiveLinkInNewTab = () => {
    if (!activeLink?.url) return;
    window.open(activeLink.url, "_blank", "noopener,noreferrer");
  };

  const closeSplitPane = () => {
    setSplitPaneEnabled(false);
    setActiveLink(null);
  };

  const loadMore = () => {
    const searchParams = new URLSearchParams(search);
    const guidelineFilter = searchParams.get("guidelinefilter");
    const searchWithin = searchParams.get("searchWithin");
    setSkipLoading(true);
    dispatch(
      combinedSearchThunk({
        searchText: previousSearchQuery.searchText,
        guidelineFacet: guidelineFilter,
        top: previousSearchQuery?.top ? previousSearchQuery.top : 10,
        skip: previousSearchQuery?.skip ? previousSearchQuery?.skip + 10 : 10,
        previousResults: searchResults[0].results,
        searchWithin: searchWithin,
      }),
    );
  };

  return (
    <div
      className={classNames(style.searchPage, "col-md-8 columnBlockLayout tabContainer regular", {
        [style.splitPaneMode]: isSplitPaneVisible,
      })}
    >
      <div className={style.mainPane}>
        <div
          className={classNames(
            {
              "size-20": isAppMode || !isDesktop !== true,
              "size-16": isAppMode || isDesktop === true,
            },
            style.tabSection,
          )}
        >
          <button
            className={classNames("size-16", {
              [style.active]: selectedTab === 0,
            })}
            onClick={() => setSelectedTab(0)}
          >
            Topic
          </button>

          <button
            className={classNames("size-16", {
              [style.active]: selectedTab === 1,
            })}
            onClick={() => setSelectedTab(1)}
          >
            Drug
          </button>
          <div
            className={classNames(
              {
                "size-16": isAppMode || !isDesktop !== true,
                "size-12 regular": isAppMode || isDesktop === true,
              },
              style.resultCount,
            )}
          >
            {/* {!isLoading && selectedTab === 0 && (
              <span className={}>{resultCount ?? 0} results</span>
            )} */}
            {(isAppMode || !isDesktop) && (
              <div className={style.iconWrapper}>
                <button type="button" onClick={() => setShowFilters(!showFilters)}>
                  {filterNumber > 0 && <span className={style.counter}>{filterNumber}</span>}
                  <i className="tg-icons-Filters size-14"></i>
                </button>
              </div>
            )}
          </div>
        </div>
        {correctedTerm && resultCount !== undefined && resultCount > 0 && (
          <div
            id="did-you-mean"
            searchTerm={correctedTerm}
            className={classNames("size-16", style.didYouMean)}
          >
            Did you mean <em>"{correctedTerm}"</em>? Showing results for this term.
          </div>
        )}
        <ul className={style.resultList}>
          {isLoading && !skipLoading ? (
            <div className={style.loader}>
              <Loader constrainedHeight={true} />
            </div>
          ) : (
            <SearchResultsList
              searchResults={searchResults}
              drugSummary={drugSummary}
              selectedTab={selectedTab}
              searchTerm={searchTerm}
              isAppMode={isAppMode}
              isWebModernisationEnabled={isWebModernisationEnabled}
              noResultsMessage={noResultsMessage}
              resultCount={resultCount}
            />
          )}
        </ul>
        {selectedTab === 1 && showAdPhaFrameTester && (
          <AdPhaFrameTester
            splitPaneEnabled={splitPaneEnabled}
            setSplitPaneEnabled={setSplitPaneEnabled}
            activeLink={activeLink}
            setActiveLink={setActiveLink}
          />
        )}
        {(isAppMode || !isDesktop) &&
          !isLoading &&
          resultCount > searchResults[selectedTab]?.results?.length && (
            <button
              className={classNames("size-14 medium", style.loadMore)}
              onClick={loadMore}
              data-testid="load-more"
            >
              <i className={classNames("tg-icons-ChevronDown", style.loadMoreIcon)}></i>
              More search results
            </button>
          )}
        {(isAppMode || !isDesktop) && (
          <AnimatedPopup show={showFilters}>
            <AppSearchFilters setShowFilters={setShowFilters} search={search} navigate={navigate} />
          </AnimatedPopup>
        )}
      </div>

      {isSplitPaneVisible && (
        <aside className={style.splitPaneFramePane} aria-label="AdPha split pane viewer">
          <div className={style.splitPaneFrameHeader}>
            <h3 className="size-16">{activeLink.label}</h3>
            <div className={style.splitPaneFrameActions}>
              <button type="button" onClick={openActiveLinkInNewTab}>
                Open In New Tab
              </button>
              <button type="button" onClick={closeSplitPane} aria-label="Close tester frame">
                Close
              </button>
            </div>
          </div>
          <div className={style.splitPaneFrameWrapper}>
            <iframe
              key={`split-pane-${activeLink.id}`}
              src={activeLink.url}
              title={`${activeLink.label} embedded`}
              className={style.splitPaneIframe}
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
              sandbox="allow-forms allow-modals allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts allow-storage-access-by-user-activation allow-top-navigation allow-top-navigation-by-user-activation"
            ></iframe>
          </div>
        </aside>
      )}
    </div>
  );
};

EnhancedSearchResults.propTypes = {
  search: PropTypes.string,
  isAppMode: PropTypes.bool,
  selectedTab: PropTypes.number,
  setSelectedTab: PropTypes.func,
};

export default EnhancedSearchResults;
